import { Link } from 'react-router-dom'

import { Skeleton } from '@/components/ui/Skeleton'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { useFormat } from '@/lib/format'

import { useTrends } from '../hooks'

/**
 * 趋势话题（D73）。按参与的作者数排，所以副标题先写「几个人在聊」再写帖子数。
 * 一个都没有时整块不渲染 —— 空的「趋势」比没有更显得冷清。
 */
export function TrendsList({
  limit = 5,
  compact = false,
  className,
}: {
  limit?: number
  /** 窄屏的探索页顶上：一个话题一行，别把第一条帖子挤出屏幕 */
  compact?: boolean
  className?: string
}) {
  const t = useT()
  const { compactCount } = useFormat()
  const { data, isPending } = useTrends()

  if (!isPending && (!data || data.items.length === 0)) return null

  return (
    <section className={cn('bg-surface-subtle py-3', className)} aria-label={t('trends.title')}>
      <div className={cn('px-4 pb-1', compact && 'flex items-baseline gap-2')}>
        <h2 className={cn('font-extrabold', compact ? 'text-[17px]' : 'text-xl')}>
          {t('trends.title')}
        </h2>
        {data && (
          <p className="text-[13px] text-ink-muted">
            {data.window === '24h' ? t('trends.window24h') : t('trends.window7d')}
          </p>
        )}
      </div>
      {isPending &&
        Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="space-y-1 px-4 py-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-20" />
          </div>
        ))}
      <ol>
        {data?.items.slice(0, limit).map((trend, index) =>
          compact ? (
            <li key={trend.name}>
              <Link
                to={`/hashtag/${encodeURIComponent(trend.name)}`}
                className="flex items-baseline gap-2 px-4 py-1.5 hover:bg-surface-hover"
              >
                <span className="w-4 shrink-0 font-mono text-[13px] text-ink-muted">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1 truncate text-[15px] font-bold">
                  #{trend.display}
                </span>
                <span className="shrink-0 text-[13px] text-ink-muted">
                  {t('trends.stats', {
                    authors: compactCount(trend.author_count),
                    posts: compactCount(trend.post_count),
                  })}
                </span>
              </Link>
            </li>
          ) : (
            <li key={trend.name}>
              <Link
                to={`/hashtag/${encodeURIComponent(trend.name)}`}
                className="block px-4 py-2 hover:bg-surface-hover"
              >
                <span className="block font-mono text-[13px] text-ink-muted">{index + 1}</span>
                <span className="block truncate text-[15px] font-bold">#{trend.display}</span>
                <span className="block text-[13px] text-ink-muted">
                  {t('trends.stats', {
                    authors: compactCount(trend.author_count),
                    posts: compactCount(trend.post_count),
                  })}
                </span>
              </Link>
            </li>
          ),
        )}
      </ol>
    </section>
  )
}
